import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { db } from '../../../firebase';
import { dealerLocation, dealerPartnerNumber, matchesDealerSearch } from '../../../utils/dealerOptions';

const inputClass = 'mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:focus:ring-white';
const buttonClass = 'inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-white hover:bg-zinc-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100';
const secondaryClass = 'rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium hover:bg-zinc-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 dark:border-zinc-700 dark:hover:bg-zinc-800';

export default function Dealers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [inputs, setInputs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [run, setRun] = useState(null);
  const [notice, setNotice] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const snap = await db.collection('dealers').get();
      setItems(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch { setError('Could not load dealers. Refresh this page to try again.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void fetchAll(); }, []);
  const visible = useMemo(() => items.filter((dealer) =>
    (showArchived || dealer.active !== false) && matchesDealerSearch(dealer, query)), [items, showArchived, query]);

  const startSync = async () => {
    setSyncing(true); setError(''); setNotice('');
    const runId = `manual-${crypto.randomUUID()}`;
    setRun({ id: runId, status: 'running' });
    try {
      const result = await firebase.functions().httpsCallable('appV2/runDealerSync', { timeout: 540000 })({ runId });
      if (result.data?.error) throw new Error(result.data.message);
      setRun(result.data);
      if (result.data.status === 'failed') setError(result.data.error || 'The sync failed. Open the run for details.');
      else if (['busy', 'running'].includes(result.data.status)) setNotice('A sync is already running. Open its job history for progress.');
      else setNotice('Dealer sync completed. Open this run to review every change.');
      await fetchAll();
    } catch (failure) {
      setError(failure.message || 'Could not verify the sync result. Check job history before starting another run.');
    } finally { setSyncing(false); }
  };
  const save = async (event) => {
    event.preventDefault(); setError(''); setSaving(true);
    try {
      const partner = dealerPartnerNumber(inputs.code);
      if (!partner || !inputs.name?.trim()) throw new Error('Enter a valid dealer code and a Porsche Center name.');
      if (items.some((dealer) => dealer.id !== inputs.id && dealerPartnerNumber(dealer.code) === partner)) {
        throw new Error('That Porsche Center is already in the directory. Edit its existing record.');
      }
      const fields = Object.fromEntries(['code', 'name', 'city', 'state', 'postal']
        .map((field) => [field, String(inputs[field] || '').trim()]));
      if (inputs.id) await db.collection('dealers').doc(inputs.id).update(fields);
      else await db.collection('dealers').add({ ...fields, porschePartnerNo: partner, active: true });
      setInputs(null); await fetchAll();
    } catch (failure) { setError(failure.message || 'Could not save this dealer.'); }
    finally { setSaving(false); }
  };
  const archive = async () => {
    setSaving(true); setError('');
    try {
      await db.collection('dealers').doc(inputs.id).update({ active: false });
      setInputs(null); await fetchAll();
    } catch { setError('Could not archive this dealer.'); }
    finally { setSaving(false); }
  };

  if (inputs) return <div className="mx-auto max-w-3xl">
    <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{inputs.id ? 'Update dealer' : 'New dealer'}</h1>
    {inputs.source && <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
      Imported fields refresh from Porsche nightly. Local changes to these fields are replaced by the next sync.
    </p>}
    <form onSubmit={save} className="mt-6 space-y-4">
      {['code', 'name', 'city', 'state', 'postal'].map((field) => <label key={field} className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
        {{ code: 'Dealer code', name: 'Porsche Center', city: 'City', state: 'State', postal: 'Postal code' }[field]}
        <input className={inputClass} value={inputs[field] || ''} name={field} required={['code', 'name'].includes(field)}
          readOnly={field === 'code' && !!inputs.id}
          onChange={(event) => setInputs({ ...inputs, [field]: event.target.value })} />
      </label>)}
      <p className="text-sm text-zinc-600 dark:text-zinc-300">Porsche partner number: {inputs.porschePartnerNo || dealerPartnerNumber(inputs.code) || 'Enter a dealer code'}</p>
      {inputs.sourceData && <details className="rounded-md border border-zinc-300 p-3 dark:border-zinc-700">
        <summary className="cursor-pointer text-sm font-medium">All Porsche source details</summary>
        <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs">{JSON.stringify(inputs.sourceData, null, 2)}</pre>
      </details>}
      {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p>}
      <div className="flex flex-wrap gap-3 pt-2">
        <button className={buttonClass} disabled={saving} type="submit">{saving ? 'Saving…' : 'Save dealer'}</button>
        <button className={secondaryClass} disabled={saving} type="button" onClick={() => { setInputs(null); setError(''); }}>Cancel</button>
        {inputs.id && inputs.active !== false && <button className={secondaryClass} disabled={saving} type="button" onClick={archive}>Archive dealer</button>}
      </div>
      {inputs.id && <p className="text-sm text-zinc-600 dark:text-zinc-300">Archiving preserves historical registrations. A dealer still present in Porsche’s feed becomes active again on the next sync.</p>}
    </form>
  </div>;

  return <div className="text-zinc-900 dark:text-zinc-100">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div><h1 className="text-2xl font-semibold tracking-tight">Dealers</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">Porsche directory sync runs nightly at 3:15 AM Central time.</p></div>
      <div className="flex flex-wrap items-center gap-3">
        <Link className={secondaryClass} to="/manage/dealers/jobs">Job history</Link>
        <button type="button" className={secondaryClass} disabled={syncing} onClick={startSync}>{syncing ? 'Syncing dealers…' : 'Sync now'}</button>
        <button type="button" className={buttonClass} onClick={() => { setInputs({}); setError(''); }}>Add dealer</button>
      </div>
    </div>
    <div aria-live="polite" className="mt-4 space-y-2 text-sm">
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert" className="text-red-700 dark:text-red-300">{error}</p>}
      {run && <Link className="inline-block underline underline-offset-4" to={`/manage/dealers/jobs/${run.id}`}>View {run.status === 'busy' || syncing ? 'running job' : 'sync result'}</Link>}
    </div>
    <div className="mt-6 flex flex-wrap items-end gap-4">
      <label className="min-w-0 flex-1 text-sm font-medium">Search dealers
        <input type="search" className={inputClass} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, city, state, postal code or dealer code" />
      </label>
      <label className="flex items-center gap-2 py-2 text-sm"><input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />Show archived</label>
    </div>
    <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300" role="status">{loading ? 'Loading dealers…' : `${visible.length} dealers`}</p>
    <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
      <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-700">
        <thead className="bg-zinc-100 dark:bg-zinc-800"><tr>{['Porsche Center', 'Dealer code', 'Partner number', 'Location', 'Status'].map((label) =>
          <th key={label} scope="col" className={`px-4 py-3 text-left text-xs font-semibold ${label === 'Location' ? 'hidden md:table-cell' : ''}`}>{label}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">{visible.map((dealer) => <tr key={dealer.id} className="hover:bg-zinc-100 dark:hover:bg-zinc-800">
          <td className="px-4 py-3 text-sm"><button type="button" className="text-left font-medium underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2" onClick={() => { setInputs(dealer); setError(''); }}>{dealer.name || dealer.code}</button>
            <span className="mt-1 block text-xs text-zinc-600 md:hidden dark:text-zinc-300">{dealerLocation(dealer)}</span></td>
          <td className="px-4 py-3 text-sm tabular-nums">{dealer.code}</td>
          <td className="px-4 py-3 text-sm tabular-nums">{dealer.porschePartnerNo || dealerPartnerNumber(dealer.code) || '—'}</td>
          <td className="hidden px-4 py-3 text-sm md:table-cell">{dealerLocation(dealer) || '—'}</td>
          <td className="px-4 py-3 text-sm">{dealer.active === false ? 'Archived' : 'Active'}</td>
        </tr>)}
          {!visible.length && !loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-600 dark:text-zinc-300">{query ? 'No dealers match your search.' : 'No dealers to display.'}</td></tr>}
        </tbody>
      </table>
    </div>
  </div>;
}
